/**
 * LicensorConsoleGridTab — GridTab + RowContextDelegate for LicensorConsole.
 * Extracted from LicensorConsole.jsx to reduce file complexity.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ERPAdvancedGrid   from './erp/ERPAdvancedGrid';
import useERPContextMenu from './erp/ERPContextMenu';
import ERPEditModal      from './erp/ERPEditModal';
import { erpToast }      from './erp/ERPToast';
import { Skeleton, ErrorBanner } from './LicensorConsolePrimitives';
import { API, apiFetch } from '../lib/api';

// ── RowContextDelegate ────────────────────────────────────────────────────────
export function RowContextDelegate({ gridTestId, rows, idField, onRowContextMenu }) {
  useEffect(() => {
    const handler = (e) => {
      const tr = e.target.closest(`tr[data-testid^="${gridTestId}-row-"]`);
      if (!tr) return;
      const id  = tr.getAttribute('data-testid').replace(`${gridTestId}-row-`, '');
      const row = rows.find(r => String(r[idField]) === id);
      if (row) onRowContextMenu(e, row);
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, [gridTestId, rows, idField, onRowContextMenu]);
  return null;
}

// ── GridTab ───────────────────────────────────────────────────────────────────
export function GridTab({
  endpoint, testIdBase, title, idField, nameField, columns,
  mutationTable, pk, copyKeys, voidAction, onSelect
}) {
  const [allRows,   setAllRows]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [page,      setPage]      = useState(1);
  const [pageSize,  setPageSize]  = useState(20);
  const [sortField, setSortField] = useState(null);
  const [sortDir,   setSortDir]   = useState('asc');
  const [filters,   setFilters]   = useState({});
  const [editing,   setEditing]   = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`${API}${endpoint}`)
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setAllRows(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const filteredSorted = useMemo(() => {
    let rows = allRows;
    const active = Object.entries(filters).filter(([, v]) => v != null && String(v).trim() !== '');
    if (active.length) rows = rows.filter(r => active.every(([k, v]) => String(r[k] ?? '').toLowerCase().includes(String(v).toLowerCase())));
    if (sortField) {
      rows = [...rows].sort((a, b) => {
        const av = a[sortField], bv = b[sortField];
        if (av == null && bv == null) return 0;
        if (av == null) return 1; if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      });
    }
    return rows;
  }, [allRows, filters, sortField, sortDir]);

  const total     = filteredSorted.length;
  const paginated = useMemo(() => filteredSorted.slice((page - 1) * pageSize, page * pageSize), [filteredSorted, page, pageSize]);

  const onSort = (field) => { if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(field); setSortDir('asc'); } };

  const onEdit = useCallback((row, mode = 'edit') => {
    if (mode === 'view' || !mutationTable) { setEditing({ row, mode: 'view' }); return; }
    setEditing({ row, mode: 'edit' });
  }, [mutationTable]);

  const onVoid = useCallback(async (row) => {
    if (!voidAction) return;
    const reason = window.prompt(`Reason to ${voidAction.label} for ${row[idField]}?`, 'Voided by Super Admin');
    if (reason == null) return;
    try {
      const res  = await apiFetch(`${API}${voidAction.endpoint(row)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) });
      const data = await res.json();
      if (!res.ok || data.error) erpToast.error(`Failed: ${data.error || res.status}`);
      else { erpToast.success(`${voidAction.label} successful`); load(); }
    } catch (e) { erpToast.error(`Failed: ${e.message}`); }
  }, [voidAction, idField, load]);

  const onOpenInNewTab = useCallback((row) => {
    const params = new URLSearchParams({ table: testIdBase, id: String(row[idField] ?? '') });
    window.open(`${window.location.origin}/?detail=${encodeURIComponent(params.toString())}`, '_blank');
  }, [testIdBase, idField]);

  const onPreview = useCallback((row) => {
    const lines = Object.entries(row).map(([k, v]) => `${k}: ${v ?? ''}`).join('\n');
    const w = window.open('', '_blank');
    w.document.write(`<pre style="font-family:monospace;font-size:12px;padding:20px;white-space:pre-wrap">${lines.replace(/</g, '&lt;')}</pre>`);
  }, []);

  const onEmail = useCallback((row) => {
    const email = row.EmailId || row.Email || row.email;
    if (!email) { erpToast.warning('No email on this row'); return; }
    window.location.href = `mailto:${email}?subject=Regarding ${row[idField]}`;
  }, [idField]);

  const { onRowContextMenu, ContextMenuPortal } = useERPContextMenu({
    idField, copyKeys: copyKeys || [],
    onView:    (r) => onEdit(r, 'view'),
    onEdit:    mutationTable ? (r) => onEdit(r, 'edit') : undefined,
    onVoid:    voidAction ? onVoid : undefined,
    onPreview, onPrint: () => window.print(),
    onExport:  (r) => { const b = new Blob([Object.entries(r).map(([k, v]) => `${k}: ${v}`).join('\n')], { type: 'text/plain' }); const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = `${r[idField] || 'row'}.txt`; a.click(); },
    onEmail, onOpenInNewTab
  });

  if (loading && !allRows.length) return <Skeleton label={`Loading ${title}...`} />;

  return (
    <div data-testid={`${testIdBase}-grid-tab`}>
      {error && <ErrorBanner error={error} onRetry={load} />}
      <ERPAdvancedGrid
        title={title} columns={columns} data={paginated} total={total} idField={idField} nameField={nameField}
        page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize}
        sortField={sortField} sortDir={sortDir} onSort={onSort}
        filters={filters} onFilterChange={(f) => { setFilters(f); setPage(1); }}
        loading={loading}
        onEdit={(row, mode) => mode === 'view' ? onEdit(row, 'view') : (mutationTable ? onEdit(row, 'edit') : onEdit(row, 'view'))}
        onVoid={voidAction ? onVoid : undefined}
        onPreview={onPreview} onEmail={onEmail}
        onRowExport={(r) => { const b = new Blob([Object.entries(r).map(([k, v]) => `${k}: ${v}`).join('\n')], { type: 'text/plain' }); const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = `${r[idField] || 'row'}.txt`; a.click(); erpToast.success('Row exported'); }}
        onRowClick={onSelect} showAddNew={false} testId={testIdBase}
        cellRenderer={(field) => { if (field === 'onRowContextMenu') return null; return null; }}
      />
      <RowContextDelegate gridTestId={testIdBase} rows={paginated} idField={idField} onRowContextMenu={onRowContextMenu} />
      {ContextMenuPortal}
      {editing && (
        <ERPEditModal
          table={mutationTable || 'ClientMaster'} row={editing.row} pk={pk}
          readOnly={editing.mode === 'view' || !mutationTable}
          title={`${editing.mode === 'view' ? 'View' : 'Edit'} ${title} — ${editing.row[idField] ?? ''}`}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
